// ─────────────────────────────────────────────────────────────────
// Sehat AI — Google Places API Utilities
// ─────────────────────────────────────────────────────────────────

import config from '../config';

// All Google Maps requests go through our CRA dev-server proxy
// so there is no CORS issue and no dependency on corsproxy.io
function googleApi(path) {
  return `/api/google${path}`;
}

/**
 * Calculate distance between two lat/lng points
 */
export function calcDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`;
}

function mapPlace(place, lat, lng) {
  return {
    name: place.name,
    clinic: place.name,
    address: place.vicinity || place.formatted_address || 'Address not available',
    rating: place.rating || 4.0,
    reviews: place.user_ratings_total || 0,
    distance: place.geometry?.location
      ? calcDistance(lat, lng, place.geometry.location.lat, place.geometry.location.lng)
      : 'Nearby',
    placeId: place.place_id,
    open: place.opening_hours?.open_now ?? null,
    phone: null,
    available: null,
    fee: null,
    exp: null,
    isReal: true,
  };
}

async function nearbySearch(lat, lng, keyword, type = '') {
  const key = config.GOOGLE_API_KEY;
  const radius = 10000; // 10 km — wider net for Indian cities
  const typePart = type ? `&type=${type}` : '';
  const path = `/maps/api/place/nearbysearch/json?location=${lat},${lng}&radius=${radius}${typePart}&keyword=${encodeURIComponent(keyword)}&key=${key}`;
  const res = await fetch(googleApi(path));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.status === 'REQUEST_DENIED') throw new Error('Invalid Google API Key');
  return data.results || [];
}

async function textSearch(lat, lng, query) {
  const key = config.GOOGLE_API_KEY;
  const path = `/maps/api/place/textsearch/json?query=${encodeURIComponent(query)}&location=${lat},${lng}&radius=10000&key=${key}`;
  const res = await fetch(googleApi(path));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.status === 'REQUEST_DENIED') throw new Error('Invalid Google API Key');
  return data.results || [];
}

/**
 * Search for doctors/clinics — uses a 3-step cascade to maximise results in
 * Indian cities where `type=doctor` coverage is sparse.
 *
 *  1. Nearby Search  — keyword=specialty, type=doctor (most precise)
 *  2. Nearby Search  — keyword="specialty doctor clinic", no type filter
 *  3. Text Search    — query="specialty doctor in city" (broadest)
 */
export async function fetchDoctorsFromPlaces(specialty, lat, lng) {
  let results = [];

  // Step 1: strict type=doctor nearby search
  results = await nearbySearch(lat, lng, specialty, 'doctor');
  if (results.length >= 2) return results.slice(0, 5).map(p => mapPlace(p, lat, lng));

  // Step 2: keyword-only nearby search (no type restriction)
  results = await nearbySearch(lat, lng, `${specialty} doctor clinic`);
  if (results.length >= 2) return results.slice(0, 5).map(p => mapPlace(p, lat, lng));

  // Step 3: text search — most likely to find results in any city
  results = await textSearch(lat, lng, `${specialty} doctor`);
  if (!results.length) return [];

  return results.slice(0, 5).map(p => mapPlace(p, lat, lng));
}

/**
 * Fetch phone, opening hours, website and up-to-5 Google reviews for a place.
 * Returns: { formatted_phone_number, opening_hours, website, reviews, rating, user_ratings_total }
 */
export async function fetchPlaceDetails(placeId) {
  const fields = [
    'formatted_phone_number',
    'opening_hours',
    'website',
    'reviews',
    'rating',
    'user_ratings_total',
  ].join(',');
  const path = `/maps/api/place/details/json?place_id=${placeId}&fields=${fields}&key=${config.GOOGLE_API_KEY}`;
  const res = await fetch(googleApi(path));
  const data = await res.json();
  return data.result || {};
}

/**
 * Reverse geocode lat/lng to get city name
 */
export async function reverseGeocode(lat, lng) {
  const path = `/maps/api/geocode/json?latlng=${lat},${lng}&key=${config.GOOGLE_API_KEY}`;
  const res = await fetch(googleApi(path));
  const data = await res.json();
  const city = data.results?.[0]?.address_components?.find(c =>
    c.types.includes('locality')
  )?.long_name;
  return city || null;
}

/**
 * Geocode a city name → { lat, lng, city }
 * Used when the user mentions a city in chat but location permission is denied.
 */
export async function geocodeCity(cityName) {
  const query = encodeURIComponent(cityName + ', India');
  const path  = `/maps/api/geocode/json?address=${query}&key=${config.GOOGLE_API_KEY}`;
  const res   = await fetch(googleApi(path));
  const data  = await res.json();
  const result = data.results?.[0];
  if (!result) return null;
  const { lat, lng } = result.geometry.location;
  const city = result.address_components?.find(c => c.types.includes('locality'))?.long_name
    || cityName;
  return { lat, lng, city };
}
